import React, { useState, useEffect, useCallback } from 'react';
import { sentineloneApi, customersApi } from '../services/api';
import { Customer } from '../types';
import toast from 'react-hot-toast';
import './PartnerCenter.css';
import './SentinelOne.css';

// Everything on this page comes from CloudView's own cache (sentinelone_sites):
// the sites, the mappings and the billed agent quantities stay put even when
// the SentinelOne API key has expired. Only "Sync from SentinelOne", the
// agents drill-down and "Test connection" talk to the console.

interface S1Site {
  id: string;
  name: string;
  customer_name: string;
  active_licenses: number;
  total_licenses: number;
  state: string;
  sku: string;
  agent_count: number | null;
  active_agent_count: number | null;
  matched_customer_id: number | null;
  matched_customer_name: string | null;
  billed_quantity: number | null;
  mapping_id: number | null;
  last_synced: string | null;
  in_console: boolean;
  cached: boolean;
}

interface S1Agent {
  id: string;
  computer_name: string;
  os_name: string;
  agent_version: string;
  is_active: boolean;
  last_active_date: string | null;
  network_status: string;
  infected: boolean;
  site_name: string | null;
}

interface S1Config {
  source: 'stored' | 'env' | null;
  server: string;
  token_hint: string | null;
  saved_at: string | null;
}

interface S1Status {
  configured: boolean;
  config_source?: 'stored' | 'env' | null;
  connected: boolean;
  account_name?: string;
  site_count?: number;
  cached_agents: number;
  last_synced: string | null;
  last_sync: { status: string; finished_at: string | null; message: string | null } | null;
  error?: string;
}

const timeAgo = (iso: string | null | undefined) => {
  if (!iso) return 'never';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
};

const HIDDEN_KEY = 'cv-sentinelone-hidden-sites';

function SentinelOne() {
  const [status, setStatus] = useState<S1Status | null>(null);
  const [sites, setSites] = useState<S1Site[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [filter, setFilter] = useState<'all' | 'matched' | 'unmatched' | 'hidden'>('all');
  const [search, setSearch] = useState('');
  const [expandedSite, setExpandedSite] = useState<string | null>(null);
  const [siteAgents, setSiteAgents] = useState<Record<string, S1Agent[]>>({});
  const [loadingAgents, setLoadingAgents] = useState<string | null>(null);
  const [mappingSite, setMappingSite] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  // API key section (stored encrypted in CloudView; .env key is the fallback)
  const [config, setConfig] = useState<S1Config | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [cfgToken, setCfgToken] = useState('');
  const [cfgServer, setCfgServer] = useState('');
  const [cfgSaving, setCfgSaving] = useState(false);
  const loadConfig = useCallback(async () => {
    try { const c = await sentineloneApi.getConfig(); setConfig(c); setCfgServer(c.server); if (!c.source) setConfigOpen(true); }
    catch { /* non-admin or not available */ }
  }, []);
  const saveConfig = async () => {
    if (!cfgToken.trim()) { toast.error('Paste the API token first'); return; }
    setCfgSaving(true);
    try {
      const r = await sentineloneApi.saveConfig({ api_token: cfgToken.trim(), server: cfgServer.trim() || undefined });
      if (r.connected) {
        const s = r.sync;
        toast.success(s
          ? `Key works — ${s.total_sites} sites / ${s.total_agents} agents synced, ${s.lines_refreshed} quantities updated`
          : `Key works — saved${r.sync_error ? ` (sync failed: ${r.sync_error})` : ''}`, { duration: 8000 });
        setCfgToken('');
        setConfigOpen(false);
      } else {
        toast.error(`Key saved but the console rejected it: ${r.error}`, { duration: 10000 });
      }
      await Promise.all([loadConfig(), refreshSites()]);
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Could not save the API key');
    } finally {
      setCfgSaving(false);
    }
  };
  const forgetConfig = async () => {
    if (!window.confirm('Forget the API key stored in CloudView? Cached sites and quantities are kept.')) return;
    try {
      const r = await sentineloneApi.deleteConfig();
      toast.success(r.source === 'env' ? 'Stored key removed — using the .env key' : 'Stored key removed');
      await Promise.all([loadConfig(), refreshSites()]);
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Could not remove the key');
    }
  };

  const [hidden, setHidden] = useState<Set<string>>(() => {
    try { return new Set<string>(JSON.parse(localStorage.getItem(HIDDEN_KEY) || '[]')); } catch { return new Set<string>(); }
  });
  const saveHidden = (n: Set<string>) => { try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(Array.from(n))); } catch { /* ignore */ } };
  const toggleHidden = (id: string) => setHidden(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); saveHidden(n); return n; });
  const unhideAll = () => { const n = new Set<string>(); saveHidden(n); setHidden(n); if (filter === 'hidden') setFilter('all'); };

  const loadData = useCallback(async () => {
    try {
      const [statusData, sitesData, customerData] = await Promise.all([
        sentineloneApi.getStatus(),
        sentineloneApi.getSites(),
        customersApi.list(),
      ]);
      setStatus(statusData);
      setSites(sitesData);
      setCustomers(customerData);
    } catch (error) {
      toast.error('Failed to load SentinelOne data');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadData(); loadConfig(); }, [loadData, loadConfig]);

  const refreshSites = async () => {
    try {
      const [statusData, sitesData] = await Promise.all([sentineloneApi.getStatus(), sentineloneApi.getSites()]);
      setStatus(statusData);
      setSites(sitesData);
    } catch (error) {
      toast.error('Failed to refresh sites');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await sentineloneApi.sync();
      toast.success(`Synced ${r.total_sites} sites / ${r.total_agents} agents — ${r.matched} mapped, ${r.created_mappings} new mappings, ${r.lines_refreshed} quantities updated`, { duration: 7000 });
      await refreshSites();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Sync failed — cached values kept', { duration: 8000 });
      await refreshSites();
    } finally {
      setSyncing(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await sentineloneApi.testConnection();
      if (r.connected) toast.success(`API key works — ${r.site_count} sites, ${r.total_agents} agents in the console`);
      else toast.error(`API key failed: ${r.error}`, { duration: 8000 });
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleExpandSite = async (siteId: string) => {
    if (expandedSite === siteId) { setExpandedSite(null); return; }
    setExpandedSite(siteId);
    if (!siteAgents[siteId]) {
      setLoadingAgents(siteId);
      try {
        setSiteAgents(prev => ({ ...prev, [siteId]: [] }));
        const agents = await sentineloneApi.getAgents(siteId);
        setSiteAgents(prev => ({ ...prev, [siteId]: agents }));
      } catch (error: any) {
        toast.error(error.response?.data?.detail || 'Agents are live only — the API key did not answer');
        setSiteAgents(prev => { const n = { ...prev }; delete n[siteId]; return n; });
        setExpandedSite(null);
      } finally {
        setLoadingAgents(null);
      }
    }
  };

  const handleMapSite = async () => {
    if (!mappingSite || !selectedCustomer) return;
    const site = sites.find(s => s.id === mappingSite);
    try {
      await sentineloneApi.mapSite(mappingSite, selectedCustomer, site?.name);
      toast.success('Site mapped — agents line added at the cached quantity');
      setMappingSite(null);
      setSelectedCustomer(null);
      setCustomerSearch('');
      await refreshSites();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to map site');
    }
  };

  const handleUnmapSite = async (siteId: string) => {
    try {
      await sentineloneApi.unmapSite(siteId);
      toast.success('Mapping removed');
      await refreshSites();
    } catch {
      toast.error('Failed to unmap');
    }
  };

  const shown = sites.filter(s => !hidden.has(s.id));
  const hiddenCount = sites.length - shown.length;
  const filteredSites = sites.filter(s => {
    const isHidden = hidden.has(s.id);
    if (filter === 'hidden' ? !isHidden : isHidden) return false;
    const matchesFilter =
      filter === 'all' || filter === 'hidden' ||
      (filter === 'matched' && s.matched_customer_id) ||
      (filter === 'unmatched' && !s.matched_customer_id);
    const q = search.toLowerCase();
    const matchesSearch = !q || s.name.toLowerCase().includes(q) || s.customer_name.toLowerCase().includes(q)
      || (s.matched_customer_name || '').toLowerCase().includes(q);
    return matchesFilter && matchesSearch;
  });

  const totalAgents = shown.reduce((sum, s) => sum + (s.total_licenses || 0), 0);
  const activeAgents = shown.reduce((sum, s) => sum + (s.active_licenses || 0), 0);
  const matchedCount = shown.filter(s => s.matched_customer_id).length;
  const unmatchedCount = shown.length - matchedCount;
  const billedAgents = shown.reduce((sum, s) => sum + (s.billed_quantity || 0), 0);
  const lastSync = status?.last_sync;
  const keyLooksDead = lastSync != null && lastSync.status !== 'success';

  if (loading) return <div className="partner-center-page s1-page"><div className="loading">Loading...</div></div>;

  return (
    <div className="partner-center-page s1-page">
      <div className="page-header">
        <h1>SentinelOne</h1>
        <p className="page-subtitle">Endpoint protection sites → customers. Values are cached in CloudView; a sync only updates the agent quantities on mapped customers.</p>
      </div>

      {/* Status: cache first, key second */}
      <div className="status-card">
        <div className={'status-indicator ' + (status?.connected ? 'connected' : 'disconnected')}>
          <span className="status-dot"></span>
          {status?.connected
            ? `${status.site_count} sites cached · ${status.cached_agents} agents · synced ${timeAgo(status.last_synced)}`
            : status?.configured ? 'Nothing cached yet — run a sync' : 'Nothing cached yet and no API token configured'}
        </div>
        <span className={'s1-key ' + (!status?.configured ? 'off' : keyLooksDead ? 'dead' : 'ok')}
              title={lastSync?.message || (status?.configured ? 'No sync has run yet' : 'SENTINELONE_API_TOKEN is not set')}>
          {!status?.configured ? '🔑 no API token'
            : keyLooksDead ? `🔑 last sync failed ${timeAgo(lastSync?.finished_at)} — cached values shown${status?.config_source === 'stored' ? ' (stored key)' : ' (.env key)'}`
            : lastSync ? `🔑 last sync OK ${timeAgo(lastSync.finished_at)}` : '🔑 not synced yet'}
        </span>
        <div className="status-actions">
          <button className={'btn btn-sm ' + (configOpen ? 'btn-primary' : 'btn-outline')} onClick={() => setConfigOpen(o => !o)}
                  title="Paste or replace the SentinelOne API key (stored encrypted in CloudView)">
            ⚙ API key
          </button>
          <button className="btn btn-outline btn-sm" onClick={handleTest} disabled={testing || !status?.configured} title="Check the API key without changing anything">
            {testing ? 'Testing…' : 'Test connection'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSync} disabled={syncing || !status?.configured}
                  title="Read every site from the console, cache it, auto-map by name and update the agent quantities on mapped customers. A failed read keeps the cached values.">
            {syncing ? 'Syncing…' : '↻ Sync from SentinelOne'}
          </button>
        </div>
      </div>
      {configOpen && (
        <div className="s1-config">
          <div className="s1-config-head">
            <strong>SentinelOne API key</strong>
            <span className="s1-config-src">
              {config?.source === 'stored'
                ? `Stored in CloudView (token ${config.token_hint}${config.saved_at ? `, saved ${timeAgo(config.saved_at)}` : ''})`
                : config?.source === 'env' ? `Using the server .env key (${config.token_hint}) — paste one here to override it`
                : 'No key yet — paste one to start'}
            </span>
          </div>
          <div className="s1-config-row">
            <label>Console server
              <input className="input" value={cfgServer} onChange={e => setCfgServer(e.target.value)} placeholder="apne1-swprd7.sentinelone.net" />
            </label>
            <label>API token
              <input className="input" type="password" value={cfgToken} onChange={e => setCfgToken(e.target.value)}
                     placeholder="Paste the API token from Settings → Users → your user → Generate API token"
                     onKeyDown={e => { if (e.key === 'Enter') saveConfig(); }} autoFocus />
            </label>
            <button className="btn btn-primary btn-sm" onClick={saveConfig} disabled={cfgSaving || !cfgToken.trim()}>
              {cfgSaving ? 'Saving & syncing…' : 'Save, test & sync'}
            </button>
            {config?.source === 'stored' && (
              <button className="btn btn-outline btn-sm" onClick={forgetConfig} disabled={cfgSaving}>Forget stored key</button>
            )}
          </div>
          <p className="s1-config-hint">
            The key is encrypted at rest and only used to read sites and agents. SentinelOne keys expire (default 6 months);
            when the last sync shows a failure, generate a new one in the console and paste it here — the cached sites,
            mappings and quantities stay in place until the new key syncs.
          </p>
        </div>
      )}
      {keyLooksDead && (
        <div className="s1-warn">
          The last sync failed{lastSync?.message ? `: ${lastSync.message}` : ''}. The sites, mappings and billed quantities below are the
          values from the last successful sync ({timeAgo(status?.last_synced)}). Renew the API key and sync to refresh them.
        </div>
      )}

      {/* Summary */}
      <div className="summary-row">
        <div className="summary-card s1-accent">
          <div className="summary-value">{shown.length}</div>
          <div className="summary-label">Sites{hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">{activeAgents}</div>
          <div className="summary-label">Active agents (of {totalAgents} licences)</div>
        </div>
        <div className="summary-card success">
          <div className="summary-value">{billedAgents}</div>
          <div className="summary-label">Agents on mapped customers</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">{unmatchedCount}</div>
          <div className="summary-label">Sites not mapped</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="filter-buttons">
          <button className={'filter-btn ' + (filter === 'all' ? 'active' : '')} onClick={() => setFilter('all')}>All ({shown.length})</button>
          <button className={'filter-btn ' + (filter === 'matched' ? 'active' : '')} onClick={() => setFilter('matched')}>Mapped ({matchedCount})</button>
          <button className={'filter-btn ' + (filter === 'unmatched' ? 'active' : '')} onClick={() => setFilter('unmatched')}>Unmapped ({unmatchedCount})</button>
          {hiddenCount > 0 && (
            <>
              <button className={'filter-btn ' + (filter === 'hidden' ? 'active' : '')} onClick={() => setFilter(filter === 'hidden' ? 'all' : 'hidden')}>🙈 Hidden ({hiddenCount})</button>
              {filter === 'hidden' && <button className="filter-btn" onClick={unhideAll}>Unhide all</button>}
            </>
          )}
        </div>
        <input type="text" className="search-input" placeholder="Search sites / customers…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Sites table (cache) */}
      <div className="tenants-table-container">
        <table className="tenants-table">
          <thead>
            <tr>
              <th style={{ width: '30px' }}></th>
              <th>Site</th>
              <th>Agents (active / licences)</th>
              <th>SKU</th>
              <th>State</th>
              <th>Mapped customer</th>
              <th>Billed qty</th>
              <th>Synced</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSites.length === 0 && (
              <tr><td colSpan={9} className="s1-empty">
                {sites.length === 0 ? 'No sites cached yet. Run "Sync from SentinelOne" once with a working API key.' : 'No sites match.'}
              </td></tr>
            )}
            {filteredSites.map(site => (
              <React.Fragment key={site.id}>
                <tr className={(site.matched_customer_id ? 'matched' : 'unmatched') + (site.in_console ? '' : ' s1-gone') + (site.cached && site.in_console && !site.active_agent_count ? ' s1-zero' : '')}
                    title={site.cached && site.in_console && !site.active_agent_count ? 'No active agents on this site - nothing is billed, no line in Invoice Prep' : undefined}>
                  <td>
                    <button className={'expand-btn ' + (expandedSite === site.id ? 'open' : '')} onClick={() => handleExpandSite(site.id)} title="Agents (live from the console)">▶</button>
                  </td>
                  <td className="tenant-name">
                    {site.customer_name}
                    {!site.cached && <span className="s1-tag" title="Mapped before the cache existed — quantities arrive with the next successful sync">not cached yet</span>}
                    {site.cached && !site.in_console && <span className="s1-tag warn" title="This site was not returned by the last successful sync">gone from console</span>}
                    {site.cached && site.in_console && !site.active_agent_count && <span className="s1-tag danger" title="No active agents">0 agents · not billed</span>}
                  </td>
                  <td>{site.cached ? `${site.active_licenses} / ${site.total_licenses}` : '—'}</td>
                  <td>{site.sku || '—'}</td>
                  <td><span className={'match-badge ' + (site.state === 'active' ? 'success' : 'warning')}>{site.state || '?'}</span></td>
                  <td>
                    {site.matched_customer_name
                      ? <span className="match-badge success">{site.matched_customer_name}</span>
                      : <span className="match-badge warning">Not mapped</span>}
                  </td>
                  <td className="s1-qty" title={site.matched_customer_id ? 'Quantity on the customer\'s "SentinelOne … agents" billing line' : 'Map the site to bill its agents'}>
                    {site.billed_quantity != null ? site.billed_quantity : '—'}
                  </td>
                  <td className="s1-when" title={site.last_synced || ''}>{timeAgo(site.last_synced)}</td>
                  <td>
                    <div className="btn-group">
                      {site.matched_customer_id ? (
                        <>
                          <button className="btn btn-sm btn-outline" onClick={() => { setMappingSite(site.id); setSelectedCustomer(site.matched_customer_id); }}>Change</button>
                          <button className="btn btn-sm btn-danger-outline" onClick={() => handleUnmapSite(site.id)}>Unmap</button>
                        </>
                      ) : (
                        <button className="btn btn-sm btn-outline" onClick={() => setMappingSite(site.id)}>Map</button>
                      )}
                      <button className="btn btn-sm btn-outline s1-hide" onClick={() => toggleHidden(site.id)}
                              title={hidden.has(site.id) ? 'Unhide' : 'Hide this site from the list and counts (this browser)'}>
                        {hidden.has(site.id) ? '👁' : '🙈'}
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedSite === site.id && (
                  <tr>
                    <td colSpan={9}>
                      <div className="agents-expand">
                        {loadingAgents === site.id ? (
                          <div>Loading agents from the console…</div>
                        ) : siteAgents[site.id]?.length ? (
                          <table>
                            <thead>
                              <tr><th>Computer</th><th>OS</th><th>Version</th><th>Status</th><th>Network</th><th>Last active</th></tr>
                            </thead>
                            <tbody>
                              {siteAgents[site.id].map(a => (
                                <tr key={a.id}>
                                  <td>
                                    <span className={'agent-status-dot ' + (a.infected ? 'infected' : a.is_active ? 'active' : 'inactive')}></span>
                                    {a.computer_name}
                                  </td>
                                  <td>{a.os_name}</td>
                                  <td>{a.agent_version}</td>
                                  <td>{a.is_active ? 'Active' : 'Inactive'}</td>
                                  <td>{a.network_status}</td>
                                  <td>{a.last_active_date ? new Date(a.last_active_date).toLocaleDateString() : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div>No agents found</div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="s1-hint">
        Billing: each mapped site puts one <strong>SentinelOne &lt;SKU&gt; agents</strong> line on the customer with the cached
        active-agent count as quantity. Set the selling price once on the Price book (service type sentinelone); a sync
        never changes prices, only quantities.
      </p>

      {/* Mapping modal */}
      {mappingSite && (
        <div className="modal-overlay" onClick={() => { setMappingSite(null); setCustomerSearch(''); }}>
          <div className="modal modal-tall" onClick={e => e.stopPropagation()}>
            <h2>Map site to customer</h2>
            <p className="modal-info">
              Site: <strong>{sites.find(s => s.id === mappingSite)?.customer_name}</strong>
            </p>
            <div className="form-group">
              <label>Search customer</label>
              <input type="text" className="input" placeholder="Type to search…" value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} autoFocus />
            </div>
            <div className="form-group">
              <label>Select customer</label>
              <div className="customer-list">
                {customers
                  .filter(c => !customerSearch || c.xero_name.toLowerCase().includes(customerSearch.toLowerCase()))
                  .slice(0, 100)
                  .map(c => (
                    <div key={c.id} className={'customer-option ' + (selectedCustomer === c.id ? 'selected' : '')} onClick={() => setSelectedCustomer(c.id)}>
                      {c.xero_name}
                    </div>
                  ))}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleMapSite} disabled={!selectedCustomer}>Map</button>
              <button className="btn btn-outline" onClick={() => { setMappingSite(null); setCustomerSearch(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SentinelOne;
